import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { numberLocale } from '@/lib/language';
import { useEnforcement } from '@/hooks/useEnforcement';
import { useSubscription } from '@/hooks/useSubscription';
import { handleUpgrade, getCurrency } from '@/lib/checkout';
import { trackConversion } from '@/lib/analytics';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import {
  PLANS,
  SUPPORT_EMAIL,
  bandOptionsFor,
  billingAccountKind,
  defaultBillingCycle,
  exceedsLargestBand,
  formatPlanPrice,
  lookupForCycle,
  planDisplayName,
  planTierLabel,
  upgradeRoute,
  type BillingCycle,
  type PlanInfo,
} from '@/lib/plans';
import { ArrowUpRight, ChevronRight, Gauge, Mail } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';

interface UpgradeModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Kapazitaets-Dialog. Vier Wege (UX-Audit G1-02, A7O-06, K38, K48):
 *   - checkout:   Neukunde waehlt ein Band, Checkout startet.
 *   - bandChange: Abonnent waehlt ein Band, die Preisseite oeffnet den
 *                 Bandwechsel (`/pricing?band=`), nie ein zweiter Checkout.
 *   - contact:    Sondertarif (D7) oder kein Band deckt den Bestand: Kontakt.
 *   - owner:      Mitglied einer fremden MC: nur der Eigentuemer kann buchen.
 * Rendert bei `open` IMMER einen Dialog. Vorher gab es `return null` ohne
 * Band, und CAPACITY_REACHED blieb fuer Agenturkonten komplett stumm.
 *
 * Kein `useNavigate` hier: `UsageIndicator.test.tsx` rendert den Dialog ohne
 * Router, und Links stehen nur im geoeffneten Dialog im Baum.
 */
export const UpgradeModal: React.FC<UpgradeModalProps> = ({ open, onClose }) => {
  const { t, i18n } = useTranslation('enforcement');
  const {
    count,
    limit,
    plan,
    nextPlan,
    recommendedPlan,
    totalProducts,
    usagePct,
    phase,
    isMemberOfForeignMc,
    billedOwnerEmail,
    billedPlanIsSpecial,
  } = useEnforcement();
  const { subscription } = useSubscription();
  const [loadingLookup, setLoadingLookup] = useState<string | null>(null);
  /**
   * CRO-Entscheidung (Dennis, 21.08.2026): Neuabschluesse starten mit
   * Jahresabrechnung, Bestandskunden auf ihrem aktuellen Intervall. Die
   * bewusste Wahl am Schalter gewinnt ueber den Default.
   */
  const [pickedCycle, setPickedCycle] = useState<BillingCycle | null>(null);
  const cycle = pickedCycle ?? defaultBillingCycle(subscription);
  const language = i18n?.language;

  // Solange die eigene Abo-Zeile noch laedt, weiss der Enforcement-Hook
  // (dasselbe Konto, wenn kein fremdes MC gewaehlt ist) schon, ob es ein
  // Sondertarif ist. Das gewinnt.
  const kind = billedPlanIsSpecial && !isMemberOfForeignMc ? 'special' : billingAccountKind(subscription);
  const route = upgradeRoute(kind, isMemberOfForeignMc);
  const effectivePlan = recommendedPlan || nextPlan;
  const bandOptions = useMemo<PlanInfo[]>(
    () => (route === 'checkout' || route === 'bandChange' ? bandOptionsFor(effectivePlan) : []),
    [route, effectivePlan],
  );
  const showBands = bandOptions.length > 0;
  const tooBigForBands = showBands && exceedsLargestBand(totalProducts);

  // Titel und Balken zeigen den GEMESSENEN Zustand: der Dialog oeffnet auch
  // aus dem UsageIndicator bei YELLOW und bei der GREEN-Kapazitaetswarnung.
  const nf = (n: number) => n.toLocaleString(numberLocale(language));
  const title =
    phase === 'RED'
      ? t('modal.title')
      : totalProducts > limit
        ? t('banner.overLimit', { total: nf(totalProducts), limit: nf(limit) })
        : t('banner.nearLimit');
  const barValue = Math.min(Math.max(usagePct, 0), 100);
  const barColor =
    phase === 'RED' ? 'bg-destructive' : phase === 'YELLOW' ? 'bg-warning' : 'bg-primary';

  useEffect(() => {
    if (open) trackConversion('upgrade_modal_shown', { plan, target: effectivePlan?.tier ?? route });
  }, [open, plan, effectivePlan?.tier, route]);

  const currency = getCurrency();
  const planLabel = planTierLabel(plan, language, t);

  // Ohne Band-Vorschlag gibt es drei verschiedene Lagen, und nur eine davon
  // heisst "kein Band deckt den Bestand": das entscheidet allein
  // `exceedsLargestBand`. Sonst deckt das eigene Band den Bestand (YELLOW
  // unter dem Limit) oder der Kunde steht schon im groessten Band.
  const largestBandTier = PLANS[PLANS.length - 1].tier;
  let explanation: string | null = null;
  let ownBandCovers = false;
  if (route === 'owner') explanation = t('modal.ownerBody', { email: billedOwnerEmail ?? '' });
  else if (route === 'contact') explanation = t('modal.specialBody', { plan: planLabel });
  else if (!showBands) {
    if (exceedsLargestBand(totalProducts)) {
      explanation = t('modal.noBandCovers', { total: nf(totalProducts) });
    } else if (plan === largestBandTier) {
      explanation = t('modal.alreadyLargestBand', { limit: nf(limit) });
    } else {
      explanation = t('modal.ownBandCovers', { total: nf(totalProducts) });
      ownBandCovers = true;
    }
  }

  const offerContact = (explanation !== null && route !== 'owner' && !ownBandCovers) || tooBigForBands;

  const onCheckout = async (band: PlanInfo) => {
    const lookupKey = lookupForCycle(band, cycle);
    trackConversion('upgrade_modal_clicked', { plan, target: band.tier });
    setLoadingLookup(lookupKey);
    const res = await handleUpgrade(lookupKey, currency);
    if (!res.redirected) setLoadingLookup(null);
  };

  const rowClass = (isRecommendedBand: boolean) =>
    `w-full flex items-center justify-between gap-3 rounded-lg border p-3 text-left transition-colors disabled:opacity-60 ${
      isRecommendedBand
        ? 'border-primary/40 bg-primary/5 hover:bg-primary/10'
        : 'border-border hover:border-primary/30 hover:bg-accent/50'
    }`;

  const rowContent = (band: PlanInfo, isRecommendedBand: boolean, isLoading: boolean) => (
    <>
      <span className="flex items-center gap-2 min-w-0">
        <span className="font-semibold text-sm truncate">{planDisplayName(band, language, t)}</span>
        {isRecommendedBand && (
          <span className="text-[10px] font-medium bg-primary/10 text-primary px-2 py-0.5 rounded-full shrink-0">
            {t('status.recommended', { ns: 'common' })}
          </span>
        )}
      </span>
      <span className="flex items-center gap-2 shrink-0 text-sm">
        <span className="font-bold">
          {formatPlanPrice(band, currency, cycle, language)}
          <span className="font-normal text-muted-foreground">
            {cycle === 'yearly' ? t('modal.perYear') : t('modal.perMonth')}
          </span>
        </span>
        {isLoading ? <Spinner size="sm" /> : <ArrowUpRight className="h-4 w-4 text-primary" />}
      </span>
    </>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gauge className="h-5 w-5 text-primary" aria-hidden="true" />
            {title}
          </DialogTitle>
          <DialogDescription>
            {t('modal.managed', { formatted: nf(count), limit: nf(limit) })}
            {totalProducts > limit && <> {t('modal.totalProducts', { total: nf(totalProducts) })}</>}
            <span className="mt-1 block">{t('modal.repeatHint')}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <Progress value={barValue} className="h-3" indicatorClassName={barColor} />

          {explanation !== null && (
            <p className="text-sm" data-testid="upgrade-modal-explanation">{explanation}</p>
          )}

          {showBands && (
            <>
              <div className="flex items-center gap-3">
                <Label
                  htmlFor="upgrade-modal-billing-toggle"
                  className={`text-sm ${cycle === 'monthly' ? 'font-semibold' : 'text-muted-foreground'}`}
                >
                  {t('monthly', { ns: 'pricing' })}
                </Label>
                <Switch
                  id="upgrade-modal-billing-toggle"
                  checked={cycle === 'yearly'}
                  onCheckedChange={(checked) => setPickedCycle(checked ? 'yearly' : 'monthly')}
                />
                <Label
                  htmlFor="upgrade-modal-billing-toggle"
                  className={`text-sm ${cycle === 'yearly' ? 'font-semibold' : 'text-muted-foreground'}`}
                >
                  {t('yearly', { ns: 'pricing' })}
                </Label>
              </div>

              {route === 'bandChange' && (
                <p className="text-xs text-muted-foreground">{t('modal.bandChangeHint')}</p>
              )}

              <div className="space-y-2" data-testid="upgrade-modal-band-list">
                {bandOptions.map((band) => {
                  const isRecommendedBand = band.tier === effectivePlan?.tier;
                  if (route === 'bandChange') {
                    return (
                      <Link
                        key={band.tier}
                        to={`/pricing?band=${band.tier}`}
                        onClick={onClose}
                        className={rowClass(isRecommendedBand)}
                      >
                        {rowContent(band, isRecommendedBand, false)}
                      </Link>
                    );
                  }
                  const isLoading = loadingLookup === lookupForCycle(band, cycle);
                  return (
                    <button
                      key={band.tier}
                      type="button"
                      onClick={() => void onCheckout(band)}
                      disabled={loadingLookup !== null}
                      className={rowClass(isRecommendedBand)}
                    >
                      {rowContent(band, isRecommendedBand, isLoading)}
                    </button>
                  );
                })}
              </div>

              <p className="text-xs text-muted-foreground">
                {cycle === 'yearly' ? t('modal.annualDiscountApplied') : t('modal.annualDiscount')}
              </p>

              {tooBigForBands && (
                <p className="text-sm" data-testid="upgrade-modal-too-big">
                  {t('modal.noBandCovers', { total: nf(totalProducts) })}
                </p>
              )}
            </>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {offerContact && (
            <Button asChild variant="outline" className="w-full sm:w-auto gap-2">
              <a href={`mailto:${SUPPORT_EMAIL}`}>
                <Mail className="h-4 w-4" aria-hidden="true" />
                {t('modal.contactCta')}
              </a>
            </Button>
          )}
          <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">
            {explanation !== null ? t('modal.ok') : t('modal.later')}
          </Button>
        </DialogFooter>

        {(showBands || ownBandCovers) && (
          <div className="text-center pt-1">
            <Link
              to="/pricing"
              onClick={onClose}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
            >
              {t('modal.seeAllBands')}
              <ChevronRight className="h-3 w-3" />
            </Link>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
